"use client";
import {
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import * as Icons from "@mui/icons-material";
import DebateAvatar from "@/components/common/DebateAvatar";
import { useCommentsStore } from "@/stores/commentsStore";
import { useSession } from "@/hook/useSession";
import { Link } from "@/i18n/routing";
import { useD3ClientPage } from "@/hook/useD3ClientPage";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useD3Fetch } from "@/hook/useD3Fetch";
import { useLocale, useTranslations } from "next-intl";
import { useNotifications } from "../provider/NotificationsProvider";
import EmojiSwiper from "./EmojiSwiper";
import { safeT } from "@/utils/safeT";

export default function CommentInput({ lang, debateId }: { lang: string; debateId: string }) {
  const {
    comment,
    commentInput,
    actionType,
    inputPlaceholder,
    setComment,
    setCommentInput,
    setFocusInput,
    setActionType,
    triggerRefresh,
    isOpenReplies,
    toggleReplies,
  } = useCommentsStore();
  const { session } = useSession();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();
  const d3Fetch = useD3Fetch();
  const t = useTranslations();
  const notification = useNotifications();
  const theme = useTheme();
  const locale = useLocale();
  const inputContainerRef = useRef<HTMLDivElement>(null);

  // 좋아요/싫어요 업데이트 Mutation
  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      // 실제 API 호출로 대체
      type CommentPayload = {
        lang: string;
        debateId: string;
        content: string;
        id?: string;
        parentId?: string;
        mentionedUserId?: string;
        commentLang: string;
      };

      const payload: CommentPayload = { lang, debateId, content: commentInput, commentLang: locale };
      if (actionType === "edit" && comment?.id) {
        payload.id = comment.id;
        payload.parentId = "";
      } else if (actionType === "reply" && comment?.id) {
        payload.id = "";
        payload.parentId = comment.id;
      } else if (actionType === "mention" && comment?.parentId) {
        payload.id = "";
        payload.parentId = comment.parentId;
        payload.mentionedUserId = comment.userId;
      }
      const response = await d3Fetch.post(`/comments/save`, payload);
    },
    onSuccess: () => {
      // 캐시 업데이트
      setComment();
      setCommentInput("");
      triggerRefresh();
      switch (actionType) {
        case "new":
          notification.show(
            safeT(t, "ui.message.commentAdded", {
              defaultMessage: "댓글이 추가 되었습니다.",
            })
          );
          break;
        case "edit":
          notification.show(
            safeT(t, "ui.message.commentEdited", {
              defaultMessage: "댓글이 수정 되었습니다.",
            })
          );
          break;
        case "reply":
          notification.show(
            safeT(t, "ui.message.replyAdded", {
              defaultMessage: "답글이 추가 되었습니다.",
            })
          );
          if (comment?.id) {
            if (!isOpenReplies(comment?.id)) {
              toggleReplies(comment.id);
            }
          }
          break;
        case "mention":
          notification.show(
            safeT(t, "ui.message.replyAdded", {
              defaultMessage: "답글이 추가 되었습니다.",
            })
          );
          if (comment?.parentId) {
            if (!isOpenReplies(comment?.parentId)) {
              toggleReplies(comment.parentId);
            }
          }
          break;
        default:
          break;
      }

      setActionType(t, "new");
    },
  });

  useEffect(() => {
    setCommentInput("");
    setActionType(t, "new");
  }, []);

  useEffect(() => {
    if (inputRef.current) {
      // focus 함수 자체를 store에 전달
      setFocusInput(() => {
        inputRef.current?.focus();
      });
    }
  }, [setFocusInput]);

  const cursorRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (cursorRef.current !== null && inputRef.current) {
      const pos = cursorRef.current;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(pos, pos);
      cursorRef.current = null; // 리셋
    }
  }, [commentInput]);

  return (
    <Container
      component={Paper}
      maxWidth="md"
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        bgcolor: theme.palette.background.default,
        borderRadius: { xs: 0, sm: 2 },
        borderTop: 1,
        borderColor: theme.palette.divider,
        zIndex: "2",
        py: 0.5,
      }}
      ref={inputContainerRef}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, padding: 0 }}>
        <Box>
          {session ? (
            <DebateAvatar
              isSearch={false}
              name={session.user?.name || ""}
              src={session?.user?.picture || undefined}
              size={24}
            />
          ) : (
            <Stack
              component={Link}
              href={`/signin?callbackUrl=${encodeURIComponent("/debates/")}${debateId}`}
              direction="row"
              alignItems={"center"}
              justifyContent={"center"}
              flexWrap={"wrap"}
              spacing={0.5}
              scroll={true}
            >
              <Icons.AccountCircle />
              <Typography fontSize="10pt">{t("ui.menu.signin")}</Typography>
            </Stack>
          )}
        </Box>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          minRows={1}
          maxRows={6}
          placeholder={inputPlaceholder}
          value={commentInput}
          disabled={!session}
          onChange={(e) => setCommentInput(e.target.value)}
          sx={{
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1.5,
            "& .MuiInputBase-root": {
              paddingRight: 1,
              lineHeight: 1.4, // 텍스트 높이 계산을 명확히
              height: "auto", // 높이를 동적으로 조정
            },
            "& .MuiInputBase-input": {},
            "& .MuiInputBase-root.MuiInputBase-multiline": {
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.background.paper,
              padding: 1,
              borderRadius: 1.5,
              whiteSpace: "pre-line",
              fontSize: "1rem",
              boxSizing: "border-box",
            },
            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end" sx={{ ml: 0 }}>
                  <Button
                    disabled={!session || !commentInput.trim() || commentInput.trim().length <= 3}
                    onClick={() => mutate()}
                    loading={isPending}
                    loadingPosition="end"
                    endIcon={session && commentInput.trim().length > 3 ? <Icons.Send /> : <Icons.SendOutlined />}
                    sx={{ minWidth: 0, padding: 1 }}
                  />
                </InputAdornment>
              ),
            },
            htmlInput: {
              maxLength: 300,
            },
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 0,
          py: 0,
        }}
      >
        {/* 왼쪽: EmojiSwiper */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: actionType === "new" ? 1.2 : 0,
            minWidth: 0,
          }}
        >
          <EmojiSwiper
            commentInput={commentInput}
            setCommentInput={setCommentInput}
            inputRef={inputRef}
            cursorRef={cursorRef}
            session={session}
          />
        </Box>

        {/* 가운데: 취소 Chip */}

        {(actionType === "edit" || actionType === "reply" || actionType === "mention") && (
          <Box sx={{ width: 70, display: "flex", justifyContent: "end", minWidth: 0 }}>
            <Chip
              onClick={() => {
                setComment();
                setCommentInput("");
                setActionType(t, "new");
              }}
              onDelete={() => {
                setComment();
                setCommentInput("");
                setActionType(t, "new");
              }}
              deleteIcon={<Icons.CancelScheduleSend fontSize="small" />}
              label={
                actionType === "edit"
                  ? safeT(t, "ui.label.cancelEdit", {
                      defaultMessage: "수정취소",
                    })
                  : safeT(t, "ui.label.cancelReply", {
                      defaultMessage: "답글취소",
                    })
              }
              size="small"
              sx={{
                fontSize: "10px",
                height: 22,
                borderRadius: 14,
                padding: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "& .MuiChip-icon": {
                  marginLeft: 1,
                  marginRight: 1,
                },
                "& .MuiChip-label": {
                  paddingLeft: 0.6,
                  paddingRight: 0.7,
                },
                "& .MuiChip-deleteIcon": {
                  color: "text.secondary",
                },
              }}
            />
          </Box>
        )}

        {/* 오른쪽: 글자 수 */}
        <Box sx={{ width: 60, display: "flex", justifyContent: "flex-end", minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              textAlign: "right",
              whiteSpace: "nowrap",
            }}
          >
            {commentInput.length}
            <Typography component="span" sx={{ opacity: 0.5, ml: 0.3 }}>
              / 300
            </Typography>
          </Typography>
        </Box>
      </Box>
    </Container>
  );
}
